import { Suspense, lazy, ElementType, useMemo } from 'react';
import { Navigate, useRoutes, useLocation, RouteObject } from 'react-router-dom';
// @types
import { ApplicationCode, AuthAplication, AuthUser } from '../@type/auth';
// hooks
import useAuth from '../hooks/useAuth';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// components
import LoadingScreen from '../components/LoadingScreen';
// ----------------------------------------------------------------------

type LoadableAccess = 'all' | ApplicationCode[];

const dashRootPath = 't360';

const getApplicationPath = (application: AuthAplication) => application.ApplicationCode === 'API' ? `/${dashRootPath}/api` :
    application.ApplicationCode === 'BROKER-HUB' ? `/${dashRootPath}/broker-hub` :
        application.ApplicationCode === 'PHYSICAL-MARKETS' ? `/${dashRootPath}/physical-markets` :
            application.ApplicationCode === 'RISK-MANAGEMENT' ? `/${dashRootPath}/risk-reports` :
                application.ApplicationCode === 'MANAGEMENT' ? `/${dashRootPath}/management` :
                    '/500';

const Loadable = (Component: ElementType, access: LoadableAccess) => (props: any) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pathname } = useLocation();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { isAuthenticated, user } = useAuth();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const isDashboard = useMemo(() => pathname.includes(`/${dashRootPath}`) && isAuthenticated, [isAuthenticated, pathname]);

    return access === 'all' || (!!user && user.Applications && user.Applications.some(e => e.Role && access.some(a => a === e.ApplicationCode))) ? (
        <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
            <Component {...props} />
        </Suspense>
    ) : pathname.includes('/500') ? <Error /> : <Navigate to="/403" replace />;
};

export default function Router() {
    const { isAuthenticated, user } = useAuth();

    const initialPath = user && user.Applications && user.Applications.length > 0 ? getApplicationPath(user.Applications[0]) : isAuthenticated ? '403' : `/${dashRootPath}/`;

    const authRoutes = useMemo(() => getAuthRoutes(), []);
    const dashRoutes = useMemo(() => getDashRoutes(user), [user]);
    const errorRoutes = useMemo(() => getErrorRoutes(), []);
    const noneRoute = { path: '/none', element: <Navigate to="/" replace /> }
    const notFoundRoute = { path: '*', element: <Navigate to="/404" replace /> }
    const defaulfRoute = {
        path: '/',
        element: <Navigate to={initialPath} replace />, index: true
    }

    const routes = [authRoutes, noneRoute, dashRoutes, errorRoutes, notFoundRoute, defaulfRoute];
    return useRoutes(routes);
}

const getAuthRoutes = () => ({
    path: 'auth',
    children: [
        {
            path: 'login',
            element: (
                <GuestGuard>
                    <Login />
                </GuestGuard>
            ),
        },
        { path: 'login-unprotected', element: <Login /> }
    ],
});
const getDashRoutes = (user: AuthUser) => {
    const dashRoutes: RouteObject = {
        path: `/${dashRootPath}`,
        element: (
            <AuthGuard>
                <DashboardLayout />
            </AuthGuard>
        ),
        children: [],
    };
    if ((!user || (user.Applications && user.Applications.some(a => a.ApplicationCode === 'API'))))
        dashRoutes.children?.push({ element: <Navigate to={`/${dashRootPath}/api/api-trace`} replace />, index: true },
            {
                path: 'api',
                children: [
                    { element: <Navigate to={`/${dashRootPath}/api/api-trace`} replace />, index: true },
                    { path: 'api-trace', element: <ApiTrace /> },
                    { path: 'fusion-summary', element: <FusionSummary /> }
                ]
            });
    if ((!user || (user.Applications && user.Applications.some(a => a.ApplicationCode === 'BROKER-HUB'))))
        dashRoutes.children?.push({ element: <Navigate to={`/${dashRootPath}/broker-hub/process-monitor`} replace />, index: true },
            {
                path: 'broker-hub',
                children: [
                    { element: <Navigate to={`/${dashRootPath}/broker-hub/process-monitor`} replace />, index: true },
                    { path: 'process-monitor', element: <ProcessMonitor /> },
                    { path: 'process-status', element: <ProcessStatus /> }
                ]
            });
    if ((!user || (user.Applications && user.Applications.some(a => a.ApplicationCode === 'PHYSICAL-MARKETS'))))
        dashRoutes.children?.push({ element: <Navigate to={`/${dashRootPath}/physical-markets/daily-pricing`} replace />, index: true },
            {
                path: 'physical-markets',
                children: [
                    { element: <Navigate to={`/${dashRootPath}/physical-markets/daily-pricing`} replace />, index: true },
                    { path: 'daily-pricing', element: <DailyPricing /> },
                ]
            });
    if (!user || (user.Applications && user.Applications.some(a => a.ApplicationCode === 'RISK-MANAGEMENT'))){
        const riskReportNode1 = { element: <Navigate to={`/${dashRootPath}/risk-reports/desk-position`} replace />, index: true };

        let riskReportNode2 = {
            path: 'risk-reports',
            children: [
                { element: <Navigate to={`/${dashRootPath}/risk-reports/global-position`} replace />, index: true },
                { path: 'global-position/', element: <GlobalPosition /> },
                { path: 'desk-position/', element: <DeskPosition /> },
                { path: 'desk-position/:desk', element: <DeskPosition /> },
                { path: 'focus-report', element: <FocusReport/> },
                { path: 'position-by-desk', element: <PositionByDesk/> },
                { path: 'position-by-product', element: <PositionByProduct/> },
                { path: 'greeks-report', element: <GreeksReport /> },
            ]
        }

        dashRoutes.children?.push(riskReportNode1, riskReportNode2);
    }     
        
    if (!user || (user.Applications && user.Applications.some(a => a.ApplicationCode === 'RISK-MANAGEMENT' && a.Role === 'ADMIN')))
        dashRoutes.children?.push({ element: <Navigate to={`/${dashRootPath}/risk-management/settings`} replace />, index: true },
        {
            path: 'risk-management',
            children: [
                { element: <Navigate to={`/${dashRootPath}/risk-management/settings`} replace />, index: true },
                { path: 'settings', element: <Navigate to={`/${dashRootPath}/risk-management/settings/desks`} replace />, index: true },
                { path: 'settings/desks/', element: <Desks /> },
                { path: 'settings/portfolios', element: <Portfolios /> },
                { path: 'settings/limit-alerts/', element: <LimitAlerts /> },
                { path: 'settings/reports/', element: <Reports /> }
            ]
        });
    if ((!user || (user.Applications && user.Applications.some(a => a.ApplicationCode === 'MANAGEMENT')))) {
        dashRoutes.children?.push({ element: <Navigate to={`/${dashRootPath}/management/users`} replace />, index: true },
            {
                path: 'management',
                children: [
                    { element: <Navigate to={`/${dashRootPath}/management/users`} replace />, index: true },
                    { path: 'users', element: <Users /> },
                ]
            });
    }
    return dashRoutes;
}

const getErrorRoutes = () => ({
    path: '*',
    element: <LogoOnlyLayout />,
    children: [
        { path: '404', element: <NotFound /> },
        { path: '403', element: <NoPermission /> },
        { path: '500', element: <Error /> },
        { path: '*', element: <Navigate to="/404" replace /> },
    ],
});

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')), 'all');

// Dashboard
const ApiTrace = Loadable(lazy(() => import('../pages/api/ApiTrace')), ['API']);
const FusionSummary = Loadable(lazy(() => import('../pages/api/FusionSummary')), ['API']);
const ProcessMonitor = Loadable(lazy(() => import('../pages/broker-hub/ProcessMonitor')), ['BROKER-HUB']);
const ProcessStatus = Loadable(lazy(() => import('../pages/broker-hub/ProcessStatus')), ['BROKER-HUB']);
const DailyPricing = Loadable(lazy(() => import('../pages/physical-markets/DailyPricing')), ['PHYSICAL-MARKETS']);
const GlobalPosition = Loadable(lazy(() => import('../pages/risk-management/GlobalPosition')), ['RISK-MANAGEMENT']);
const DeskPosition = Loadable(lazy(() => import('../pages/risk-management/DeskPosition')), ['RISK-MANAGEMENT']);
const FocusReport = Loadable(lazy(() => import('../pages/risk-management/FocusReport')), ['RISK-MANAGEMENT']);
const PositionByDesk = Loadable(lazy(() => import('../pages/risk-management/PositionByDesk')), ['RISK-MANAGEMENT']);
const PositionByProduct = Loadable(lazy(() => import('../pages/risk-management/PositionByProduct')), ['RISK-MANAGEMENT']);
const GreeksReport = Loadable(lazy(() => import('../pages/risk-management/GreeksReport')), ['RISK-MANAGEMENT']);
const Desks = Loadable(lazy(() => import('../pages/risk-management/Desks')), ['RISK-MANAGEMENT']);
const Portfolios = Loadable(lazy(() => import('../pages/risk-management/Portfolios')), ['RISK-MANAGEMENT']);
const LimitAlerts = Loadable(lazy(() => import('../pages/risk-management/LimitAlerts')), ['RISK-MANAGEMENT']);
const Reports = Loadable(lazy(() => import('../pages/risk-management/Reports')), ['RISK-MANAGEMENT']);
const Users = Loadable(lazy(() => import('../pages/management/Users')), ['MANAGEMENT']);
const NotFound = Loadable(lazy(() => import('../pages/Page404')), 'all');
const NoPermission = Loadable(lazy(() => import('../pages/Page403')), 'all');
const Error = Loadable(lazy(() => import('../pages/Page500')), 'all');